import { Page } from 'components'
import { observer } from 'mobx-react'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { RoutingParams } from 'routes'
import { StepType } from 'types'
import {
  EditorTopbar,
  QuizEditor,
  SidebarCourseEditor,
  StepEditor,
  VideoEditor
} from 'views'
import { CourseStep } from 'views/course/courseStep'
import { GridEditor } from 'views/editor/gridEditor'
import { Notes } from 'views/editor/notes'
import { Course } from '../models'
import { CourseSettings } from '../views/editor/courseSettings'

/**
 * Shows and manages the different editors for a selected course.
 *
 * @type Editor
 */

export const Editor = observer(() => {
  const params: RoutingParams = useParams<RoutingParams>();
  const { t } = useTranslation();
  
  const [course,  setCourse]  = React.useState<Course>(null);
  const [preview, setPreview] = React.useState<boolean>(false);

  React.useEffect(() => {
    Course.get(Number(params.id)).then((c: Course) => {
      setCourse(c);
      c.loadSteps();
    })
  }, [params]);

  const topbarAction = React.useCallback(async () => {
      await course?.getCurStep.loadQuiz(!preview);
      setPreview(!preview);
  }, [course, preview]);

  const previewLabel = preview ? t('back') : t('preview');

  const isOnEditingStep =
    params.view !== 'settings' && params.view !== 'notes';

  let view: ReactNode;
  if (params.view === 'settings')
    view = <CourseSettings course={course} />;
  else if (params.view === 'notes') view = <Notes course={course} />;
  else if (preview) {
    view = (
      <CourseStep 
        step     = {course.getCurStep}
        course   = {course}
        preview  = {true}
        disableNextButton = {false}
      />
    );
  } else if (course?.getCurStep?.getType === StepType.Video)
    view = <VideoEditor course={course} />;
  else if (course?.getCurStep?.getType === StepType.Quiz)
    view = <QuizEditor course={course} />;
  else if (course?.getCurStep?.getType === StepType.Grid)
    view = <GridEditor course={course} />;
  else if (course?.getCurStep?.getType === StepType.Read)
    view = <StepEditor course={course} />;
  else view = null;

  React.useEffect(() => {
    if (course?.getCurStep) {
      course.getCurStep.loadDocuments();
      if(course.getCurStep.isGrid) 
        course.getCurStep.getGridSteps.forEach(s => s.loadDocuments());
    }
  }, [course?.getCurStep]);

  return (
    <Page
      sidebar = {<SidebarCourseEditor course={course} />}
      topbar  = {
        <EditorTopbar
          onAction       = {topbarAction}
          actionLabel    = {previewLabel}
          hideAction     = {params.view === 'settings' || params.view === 'notes'}
          title          = {course?.title}
          showSettings   = {isOnEditingStep}
          currentStep    = {course?.getCurStep}
          canEdit        = {course?.canEdit}
          showBackButton = {course?.getCurStep?.getParent?.isGrid && course?.getCurStep?.isSub}
          onBack         = {() => course.setCurStep(course?.getCurStep?.getParent)}
          actionDisabled = {
            course?.getCurStep?.getType === StepType.Quiz &&
            !course.getCurStep.quiz?.isValid &&
            !preview
          }
        />
      }
    >
      {view}
    </Page>
  )
});
